import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Clock,
  ArrowLeft,
  Mail,
  Settings,
  MapPin,
  CalendarDays,
  Users,
  CalendarClock,
  Fingerprint,
  QrCode,
  Umbrella,
  MessageSquareWarning,
  FileText,
  FileSpreadsheet,
  BellRing,
  Users2,
  CreditCard
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SETUP_STEPS_ES = [
  { icon: Mail, title: 'Confirma tu email', body: 'Al registrarte te enviamos un enlace de confirmación. Ábrelo para activar tu cuenta y tu prueba gratuita de 14 días — no hace falta tarjeta.' },
  { icon: Settings, title: 'Datos de tu empresa', body: 'En Ajustes rellena el nombre legal, CIF, dirección, logo y zona horaria. Es lo que aparecerá en los informes que uses ante una inspección de trabajo.' },
  { icon: MapPin, title: 'Ubicaciones y geolocalización', body: 'En Ubicaciones añade cada sede con sus coordenadas y el radio permitido para fichar. Los fichajes fuera de rango quedan marcados automáticamente.' },
  { icon: CalendarDays, title: 'Calendario laboral y festivos', body: 'Crea el calendario anual de tu empresa y marca los festivos nacionales, autonómicos y locales que apliquen.' },
  { icon: Users, title: 'Da de alta a tus empleados', body: 'En Empleados añade a tu equipo. Dales acceso con usuario y contraseña solo si necesitan entrar a la app (por ejemplo, para pedir vacaciones); si solo van a fichar, basta con un PIN de 4 dígitos para el kiosko.' },
  { icon: CalendarClock, title: 'Turnos y plantillas', body: 'Crea plantillas de turno reutilizables y asígnalas a cada empleado, incluso en secuencias de varias semanas.' },
  { icon: Fingerprint, title: 'Activa el fichaje', body: 'Elige cómo va a fichar tu equipo: kiosko compartido por PIN, código QR en pantalla, o ambos. La URL del kiosko se copia desde el listado de organizaciones o desde Ajustes.' },
];

const SETUP_STEPS_EN = [
  { icon: Mail, title: 'Confirm your email', body: 'We send a confirmation link when you sign up. Open it to activate your account and your 14-day free trial — no card required.' },
  { icon: Settings, title: 'Your company details', body: 'In Settings, fill in your legal name, tax ID, address, logo and timezone. This is what shows up on the reports you\'d use for a labor inspection.' },
  { icon: MapPin, title: 'Locations and geolocation', body: 'In Locations, add each site with its coordinates and the allowed clock-in radius. Out-of-range clock-ins are flagged automatically.' },
  { icon: CalendarDays, title: 'Work calendar and holidays', body: 'Create your company\'s yearly calendar and mark the national, regional and local holidays that apply.' },
  { icon: Users, title: 'Add your employees', body: 'In Employees, add your team. Give them a login only if they need to access the app (e.g. to request vacation); if they\'ll only clock in, a 4-digit kiosk PIN is enough.' },
  { icon: CalendarClock, title: 'Shifts and templates', body: 'Create reusable shift templates and assign them to each employee, including multi-week sequences.' },
  { icon: Fingerprint, title: 'Turn on clock-in', body: 'Choose how your team clocks in: a shared PIN kiosk, an on-screen QR code, or both. The kiosk URL can be copied from the organizations list or from Settings.' },
];

const USAGE_ITEMS_ES = [
  { icon: QrCode, title: 'Fichar', body: 'Cada trabajador ficha entrada, inicio/fin de descanso y salida por PIN, QR o geolocalización. Cada fichaje queda con hash de verificación y firma digital, listo para una inspección.' },
  { icon: Umbrella, title: 'Vacaciones y ausencias', body: 'El trabajador solicita sus vacaciones desde su perfil; tú apruebas o rechazas con un clic. Las bajas y ausencias se registran igual, con su tipo.' },
  { icon: MessageSquareWarning, title: 'Incidencias', body: '¿Alguien olvidó fichar? El trabajador puede solicitar una corrección con la hora real; tú la revisas antes de que quede como registro definitivo.' },
  { icon: BellRing, title: 'Aviso de fichaje no realizado', body: 'Si a un trabajador se le pasan 15 minutos desde el inicio de su turno sin fichar, se le avisa automáticamente a él y a su supervisor o administrador, por notificación en la app y por email.' },
  { icon: FileText, title: 'Informes', body: 'Exporta a PDF, Excel o CSV con vacaciones, horas extra, nocturnas, de domingo o festivo, y bajas — con el detalle que pide una inspección de trabajo o tu asesoría. Los días de vacaciones o baja se marcan siempre claramente, nunca como horas negativas.' },
  { icon: FileSpreadsheet, title: 'Plantillas de exportación para nómina', body: 'Cada asesoría configura su propio programa de nómina (A3, Sage, Nominaplus...) de forma distinta. En Plantillas de Exportación defines tus propias columnas, códigos y formato para que el fichero encaje exactamente con lo que pide la tuya. En Ajustes eliges cómo se calculan las horas extra en Informes, Resumen y estas plantillas: por balance neto del periodo (por defecto, compensando días de más con días de menos) o por horas brutas (sumando solo los excesos diarios) — confirmad con vuestra asesoría qué criterio encaja con el convenio colectivo.' },
  { icon: Users2, title: 'Roles y permisos', body: 'Administrador (todo), Supervisor (gestión operativa), Trabajador (su propio fichaje), Gestor (Informes y Plantillas de Exportación, y en Empleados solo ve nombre y código de nómina — pensado para vuestra asesoría) e Inspector — este último es de solo lectura para un inspector de trabajo externo y nunca cuenta como usuario de pago.' },
  { icon: CreditCard, title: 'Facturación', body: 'Desde Facturación puedes suscribirte, cambiar de plan o descargar tus facturas en cualquier momento. Tu prueba de 14 días no requiere tarjeta.' },
];

const USAGE_ITEMS_EN = [
  { icon: QrCode, title: 'Clocking in', body: 'Each employee clocks in, starts/ends breaks, and clocks out via PIN, QR or geolocation. Every record gets a verification hash and digital signature, ready for an inspection.' },
  { icon: Umbrella, title: 'Vacations and absences', body: 'Employees request time off from their profile; you approve or reject it in one click. Sick leave and other absences are logged the same way, by type.' },
  { icon: MessageSquareWarning, title: 'Incidents', body: 'Did someone forget to clock in? They can request a correction with the real time; you review it before it becomes a permanent record.' },
  { icon: BellRing, title: 'Missed clock-in alert', body: "If 15 minutes pass after a worker's shift start with no clock-in, both they and their supervisor or admin are notified automatically, in-app and by email." },
  { icon: FileText, title: 'Reports', body: 'Export to PDF, Excel or CSV with vacation, overtime, night, Sunday/holiday hours and sick leave — with the level of detail a labor inspection or your accountant asks for. Vacation and sick-leave days are always clearly labeled, never shown as negative hours.' },
  { icon: FileSpreadsheet, title: 'Payroll export templates', body: "Every accounting firm sets up its own payroll software (A3, Sage, Nominaplus...) differently. In Export Templates you define your own columns, codes and format so the file matches exactly what yours needs. In Settings you choose how overtime is calculated across Reports, Summary and these templates: net balance for the period (default, offsetting days worked over against days worked under) or gross hours (adding up only each day's excess) — confirm with your accounting firm which one matches your collective agreement." },
  { icon: Users2, title: 'Roles and permissions', body: 'Admin (everything), Supervisor (day-to-day management), Worker (their own clock-ins), Manager/Gestor (Reports and Export Templates, and only sees name and payroll code in Employees — meant for your accounting firm) and Inspector — this last one is read-only for an external labor inspector and never counts as a paid seat.' },
  { icon: CreditCard, title: 'Billing', body: 'From Billing you can subscribe, change plans or download your invoices at any time. Your 14-day trial never requires a card.' },
];

const Guide = () => {
  const { language, toggleLanguage } = useLanguage();
  const isEs = language === 'es';
  const [appName, setAppName] = useState('Regulisto');

  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/public`);
        if (response.data?.app_name) setAppName(response.data.app_name);
      } catch (error) {
        console.error('Error fetching public settings:', error);
      }
    };
    fetchAppName();
  }, []);

  const setupSteps = isEs ? SETUP_STEPS_ES : SETUP_STEPS_EN;
  const usageItems = isEs ? USAGE_ITEMS_ES : USAGE_ITEMS_EN;

  return (
    <div className="min-h-screen bg-white" data-testid="guide-page">
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold font-['Manrope'] text-slate-900">{appName}</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={toggleLanguage} className="text-slate-600" data-testid="guide-language-toggle">
              {isEs ? '🇬🇧 EN' : '🇪🇸 ES'}
            </Button>
            <Link to="/">
              <Button variant="ghost" className="gap-2 text-slate-600">
                <ArrowLeft className="w-4 h-4" />
                {isEs ? 'Volver' : 'Back'}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-16 pb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900">
          {isEs ? 'Guía de configuración y funcionamiento' : 'Setup and usage guide'}
        </h1>
        <p className="mt-4 text-slate-500">
          {isEs
            ? `Todo lo que necesitas para poner en marcha ${appName} en tu empresa y sacarle partido cada día.`
            : `Everything you need to get ${appName} up and running for your company, and use it day to day.`}
        </p>
      </section>

      {/* Setup */}
      <section className="max-w-4xl mx-auto px-6 py-10">
        <h2 className="text-xl font-bold font-['Manrope'] text-slate-900 mb-6">
          {isEs ? 'Primeros pasos' : 'Getting started'}
        </h2>
        <div className="space-y-4">
          {setupSteps.map((step, i) => (
            <div key={i} className="flex gap-4 p-5 rounded-xl border border-slate-200">
              <div className="shrink-0 w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-semibold font-['Manrope']">
                {i + 1}
              </div>
              <div>
                <h3 className="font-semibold font-['Manrope'] text-slate-900 flex items-center gap-2">
                  <step.icon className="w-4 h-4 text-indigo-600" />
                  {step.title}
                </h3>
                <p className="mt-1 text-sm text-slate-600">{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Usage */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-xl font-bold font-['Manrope'] text-slate-900 mb-6">
            {isEs ? 'Cómo funciona el día a día' : 'How it works day to day'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {usageItems.map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200">
                <div className="w-11 h-11 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="font-semibold font-['Manrope'] text-slate-900 mb-1.5">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-4">
          {isEs ? '¿Todavía no tienes cuenta?' : "Don't have an account yet?"}
        </h2>
        <Link to="/signup">
          <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-base px-8">
            {isEs ? 'Empieza gratis' : 'Start free'}
          </Button>
        </Link>
      </section>

      <footer className="border-t border-slate-100 py-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-center text-sm text-slate-400">
          © {new Date().getFullYear()} {appName}
        </div>
      </footer>
    </div>
  );
};

export default Guide;
